import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Loader, Message } from '@getro/rombo';
import { Box, Flex, Text } from 'rebass/styled-components';
import { activeNetworkSelector } from 'redux/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { useGetChargeBessSessionMutation, useGetSubscriptionsQuery } from 'services/organizationsV2';

import { SettingsLayoutHeader } from '../../../components/atoms/settingsLayoutHeader';
import { HubspotMeetingsModal } from '../../../components/molecules/hubspotMeetingsModal';
import usePageHook from '../../../hooks/usePageHook';
import { trackPlansAndBilling } from '../actions';
import { PlansBox } from './plansBox';
import { AICreditsSection } from './components/AICreditsSection';

const pageTitle = 'Plans and billing';

export const PlansAndBilling = () => {
  const [showMeetingsModal, setShowMeetingsModal] = useState(false);
  const [meetingsModalKind, setMeetingsModalKind] = useState('contacts');
  const network = useSelector(activeNetworkSelector);
  const availableAgentCredits = network?.availableAgentCredits || 0;
  const [getChargeBessSession] = useGetChargeBessSessionMutation();
  const { data: subscriptions, error, isLoading } = useGetSubscriptionsQuery(network.id);
  const dispatch = useDispatch();
  const cbInstance = useRef(null);
  const sourcingAgentEnabled = network?.features?.includes('sourcing_agent');
  const canAddCompanies = useMemo(() => {
    const sub = subscriptions?.activeSubscriptions?.find((subscription) =>
      subscription.subscriptionItems.find((item) => item?.stairstep?.unit === 'companies'),
    ); // eslint-disable-line function-paren-newline

    if (sub) {
      const activeStairstep = sub.subscriptionItems?.find((item) => item?.stairstep?.unit === 'companies');

      if (activeStairstep) {
        return activeStairstep?.stairstep?.used < activeStairstep?.stairstep?.total;
      }
    }

    return true;
  }, [subscriptions]);

  usePageHook(pageTitle);

  useEffect(() => {
    if (cbInstance.current || !network.isManager) {
      return () => {};
    }

    cbInstance.current = window.Chargebee.init({
      site: import.meta.env.VITE_CHARGEBEE_SITE,
    });

    cbInstance.current.setPortalSession(async () => {
      const { data } = await getChargeBessSession(network.id);
      return data;
    });

    return () => {};
  }, [getChargeBessSession, network]);

  useEffect(
    () => () => {
      if (cbInstance.current) {
        cbInstance.current.logout();
        cbInstance.current = null;
      }
    },
    [],
  );

  if (!network.isManager) return null;

  const handlePortal = (type) => {
    const sources = window.Chargebee.getPortalSections();

    cbInstance.current.createChargebeePortal().openSection({
      sectionType: type,
    });

    let eventName;
    switch (type) {
      case sources.PAYMENT_SOURCES:
        eventName = 'open_update_payment_method_widget';
        break;
      case sources.ADDRESS:
        eventName = 'open_update_billing_info_widget';
        break;
      case sources.BILLING_HISTORY:
        eventName = 'open_billing_history_widget';
        break;
      default:
        break;
    }

    dispatch(trackPlansAndBilling({ eventName }));
  };
  const { connectTier } = network;

  const noConnectSubscription =
    !isLoading &&
    (error ||
      subscriptions.activeSubscriptions <= 0 ||
      !subscriptions?.activeSubscriptions?.find((subscription) =>
        subscription.subscriptionItems.find(
          (item) => item?.itemPriceFamilyId === 'GetroConnect' || item?.stairstep?.unit === 'contacts',
        ),
      )); // eslint-disable-line function-paren-newline

  return (
    <Box maxWidth="600px">
      <SettingsLayoutHeader
        title={pageTitle}
        description="See your current plan and update payment and billing information."
      />
      {!isLoading && sourcingAgentEnabled && (
        <Box mt="32px">
          <AICreditsSection availableCredits={availableAgentCredits} />
        </Box>
      )}
      {isLoading && (
        <Flex mt="32px" justifyContent="center" width="100%">
          <Loader />
        </Flex>
      )}
      {noConnectSubscription && (
        <Box mt="32px">
          <PlansBox
            subscription={{
              quantity: 1,
              unitPrice: 0,
              amount: 0,
              freeQuantity: network.contactsLimit,
              itemPriceFamilyId: 'GetroConnect',
              stairstep: { unit: 'contacts', total: network.contactsLimit, used: network.contactsTotal },
            }}
            connectTier={connectTier}
            networkSlug={network.slug}
            billingPeriod={1}
            billingPeriodUnit="month"
            currencyCode="USD"
            upcomingInvoiceTotal={0}
            status="active"
          />
        </Box>
      )}
      {!isLoading && subscriptions?.activeSubscriptions?.length > 0 && (
        <Flex mt="32px" sx={{ flexDirection: 'column', gap: '16px' }}>
          {subscriptions.activeSubscriptions.map((subscription) => (
            <PlansBox
              key={subscription.id}
              subscription={subscription.subscriptionItems[0]}
              connectTier={connectTier}
              networkSlug={network.slug}
              billingPeriod={subscription.billingPeriod}
              billingPeriodUnit={subscription.billingPeriodUnit}
              currencyCode={subscription.currencyCode}
              upcomingInvoiceTotal={subscription.upcomingInvoiceTotal}
              nextBillingAt={subscription.nextBillingAt}
              coupon={subscription.coupon}
              status={subscription.status}
              currentTermEnd={subscription.currentTermEnd}
            />
          ))}
        </Flex>
      )}
      {network.contactsOverLimit > 0 && (
        <Message type="warning" mt="16px">
          <Text fontSize="14px" fontWeight="medium" color="text.dark">
            Your network has reached its contacts limit
          </Text>
          <Text fontSize="14px" mt="8px">
            Upgrade now to keep growing your network and make sure high-impact intros are not missed.
          </Text>
          <Button
            mt="8px"
            onClick={() => {
              setMeetingsModalKind('contacts');
              setShowMeetingsModal(true);
            }}
          >
            Upgrade contacts limit
          </Button>
        </Message>
      )}
      {!canAddCompanies && (
        <Message type="warning" mt="16px">
          <Text fontSize="14px" fontWeight="medium" color="text.dark">
            Your job board has reached its company limit
          </Text>
          <Text fontSize="14px" mt="8px">
            Upgrade now to keep growing your network and make sure high-impact intros are not missed.
          </Text>
          <Button
            mt="8px"
            onClick={() => {
              setMeetingsModalKind('companies');
              setShowMeetingsModal(true);
            }}
          >
            Upgrade company limit
          </Button>
        </Message>
      )}
      {!isLoading && (
        <>
          <Flex
            mt="32px"
            flexDirection={['column', 'row']}
            sx={{ gap: '16px', a: { color: 'text.main', fontSize: '14px', lineHeight: '16px' } }}
          >
            <Box
              as="a"
              onClick={() => handlePortal(window.Chargebee.getPortalSections().PAYMENT_SOURCES)}
              data-cb-type="portal"
            >
              Update payment method
            </Box>
            <Box as="a" onClick={() => handlePortal(window.Chargebee.getPortalSections().ADDRESS)} to="#">
              Update billing information
            </Box>
            <Box as="a" onClick={() => handlePortal(window.Chargebee.getPortalSections().BILLING_HISTORY)}>
              Billing history
            </Box>
          </Flex>
        </>
      )}
      {showMeetingsModal && (
        <HubspotMeetingsModal kind={meetingsModalKind} onCancel={() => setShowMeetingsModal(false)} />
      )}
    </Box>
  );
};

export default PlansAndBilling;
